/**
 * Workspace Controls → Connected accounts (ADR-029 §2): the outside accounts a
 * workspace connected, the apps it lets use each one, and the controls to take
 * either away. Every member sees the list; owners and admins (the owner of a
 * personal workspace) connect, disconnect and remove an app's access.
 */

import { useEffect, useRef, useState } from 'react';
import { Link2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { api, type ConnectionDto } from '@/lib/api';
import {
  useConnectionProviders,
  useDisconnectConnection,
  useHolderConnections,
  useRevokeConnectionGrant,
} from '@/lib/hooks/useConnections';
import { CONNECTIONS_BROADCAST_CHANNEL } from '@/arche/components/ConnectAccountSheet';
import { openConnectPopup, watchConnectPopupClosed } from '@/lib/connect-popup';

function accountLabel(connection: ConnectionDto): string {
  if (connection.accountHandle) return `@${connection.accountHandle}`;
  return connection.accountDisplayName ?? connection.providerDisplayName;
}

export function ConnectedAccountsCard({ holderId }: { holderId: string }) {
  const providers = useConnectionProviders();
  const query = useHolderConnections(holderId);
  const disconnect = useDisconnectConnection(holderId);
  const revoke = useRevokeConnectionGrant(holderId);
  const [connecting, setConnecting] = useState(false);
  /** The account a person asked to disconnect, waiting for them to confirm. */
  const [confirming, setConfirming] = useState<ConnectionDto | null>(null);
  const popupRef = useRef<Window | null>(null);
  const connections = query.data?.connections ?? [];
  const canManage = query.data?.canManage ?? false;

  // The connect popup reports back here, as it does to an app's connect sheet.
  useEffect(() => {
    if (!connecting || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(CONNECTIONS_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as { status?: string };
      setConnecting(false);
      if (data.status === 'connected') {
        void query.refetch();
        toast({ title: 'Account connected' });
      } else {
        toast({ title: 'The account was not connected', variant: 'destructive' });
      }
    };
    return () => channel.close();
  }, [connecting]);

  // Closing the window without finishing just ends the attempt.
  useEffect(() => {
    const popup = popupRef.current;
    if (!connecting || !popup) return;
    return watchConnectPopupClosed(popup, () => setConnecting(false));
  }, [connecting]);

  const connect = async (provider: string) => {
    const popup = openConnectPopup();
    if (!popup) {
      toast({ title: 'Allow pop-ups for Fias to connect an account', variant: 'destructive' });
      return;
    }
    try {
      const { authorizationUrl } = await api.startConnectionOAuth({ provider, holderId });
      popup.location.href = authorizationUrl;
      popupRef.current = popup;
      setConnecting(true);
    } catch {
      popup.close();
      toast({ title: 'Could not start connecting the account', variant: 'destructive' });
    }
  };

  const onDisconnect = async (connection: ConnectionDto) => {
    try {
      const result = await disconnect.mutateAsync(connection.connectionId);
      // Fias deleted its copy of the sign-in either way. When the provider did
      // not confirm the revoke, say so — but never advise removing Fias at the
      // provider outright: that also cuts every OTHER workspace or app using
      // the same account with Fias.
      toast({
        title: `${accountLabel(connection)} disconnected`,
        description: result.revokedAtProvider
          ? undefined
          : `Fias no longer holds its sign-in. ${connection.providerDisplayName} did not confirm the access was removed; if you no longer use this account with Fias anywhere, you can also remove Fias in your ${connection.providerDisplayName} settings.`,
      });
    } catch {
      toast({ title: 'Could not disconnect the account', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected accounts</CardTitle>
        <CardDescription>
          Outside accounts this workspace connected, and the apps it lets use them. An app acts as
          an account only after someone here lets it, asks before it posts, and never sees its
          sign-in. Disconnecting an account, or taking an app's access away, also stops anything
          that app scheduled to post with it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {query.isError && (
          <div className="flex items-center justify-between gap-4" role="alert">
            <p className="text-sm text-destructive">Could not load the connected accounts.</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        )}

        {!query.isLoading && !query.isError && connections.length === 0 && (
          <p className="text-sm text-muted-foreground">No accounts are connected.</p>
        )}

        {connections.length > 0 && (
          <ul className="flex flex-col divide-y divide-border border-y border-border">
            {connections.map((connection) => (
              <li key={connection.connectionId} className="space-y-2 py-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{accountLabel(connection)}</p>
                    <p className="text-xs text-muted-foreground">
                      {connection.providerDisplayName}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {connection.status !== 'active' && (
                      <Badge variant="destructive">Needs reconnecting</Badge>
                    )}
                    {canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={disconnect.isPending}
                        onClick={() => setConfirming(connection)}
                      >
                        Disconnect
                      </Button>
                    )}
                  </div>
                </div>
                {connection.apps.length > 0 && (
                  <ul className="space-y-1 pl-2">
                    {connection.apps.map((app, i) => {
                      // A restricted app the viewer may not see carries no id
                      // (ADR-026): it shows as "an app", and disconnecting the
                      // account is how its access goes.
                      const archeId = app.archeId;
                      return (
                        <li
                          key={archeId ?? `withheld-${i}`}
                          className="flex items-center justify-between gap-4 text-sm"
                        >
                          <span className="truncate text-muted-foreground">
                            Used by {app.name ?? 'an app'}
                            {app.current === false && ' · access needs renewing'}
                          </span>
                          {canManage && archeId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={revoke.isPending}
                              onClick={() =>
                                void revoke
                                  .mutateAsync({
                                    connectionId: connection.connectionId,
                                    archeId,
                                  })
                                  .catch(() =>
                                    toast({
                                      title: 'Could not remove the app’s access',
                                      variant: 'destructive',
                                    }),
                                  )
                              }
                            >
                              Remove access
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (providers.data?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2">
            {providers.data!.map((p) => (
              <Button
                key={p.provider}
                variant="secondary"
                size="sm"
                disabled={connecting}
                onClick={() => void connect(p.provider)}
              >
                <Link2 className="mr-2 h-4 w-4" aria-hidden />
                Connect {p.displayName}
              </Button>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect {confirming ? accountLabel(confirming) : 'this account'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every app this workspace lets use it loses access at once, and anything they scheduled
              to post with it will not be sent. Connecting it again later means asking each app's
              access again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={disconnect.isPending}
              onClick={() => {
                const connection = confirming;
                setConfirming(null);
                if (connection) void onDisconnect(connection);
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
